/**
 * The radar's controls and its portfolio arithmetic.
 *
 * ── Why the reconciliation strip exists at all ────────────────────────────────────────────────
 * The tab shipped saying "22 open roles" above 22 cards, and it was wrong: those are 22 PIPELINES,
 * and the cards under them say "5 open hires", "3 open hires". Role Prioritisation counts
 * requisitions and this tab draws postings, so the two tabs reported different numbers for what
 * read as the same thing — with nothing on screen to say why. The strip states all four figures
 * and the identity between them, permanently and without being opened, because a total nobody can
 * reconcile is a total nobody trusts.
 *
 * It uses `FactStrip` rather than a warning block: this is not a problem, it is how the portfolio
 * is shaped. Only the two lossy buckets are tinted, and only when they are non-zero.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import {
  Fact,
  FactStrip,
  FactValue,
  FIELD_SELECT,
} from '../rolePrioritisation/prioritisationUi';
import type { RadarTotals } from '../../../types/taBottleneck';
import { type RadarFilter, isFiltered } from '../../../utils/bottleneckFilters';
import { windowLabel } from '../../../utils/bottleneckRadar';

/**
 * Cluster and department, as two labelled native selects.
 *
 * ⚠️ Native `<select>` via `FIELD_SELECT`, which is the SAME documented deviation Role
 * Prioritisation's support form and model editor take, and for the same reason: the design-system
 * `Select` trigger renders `body-16-regular` and sizes to its content, so it is a 16px control in a
 * 13px page and it wraps a name like "Core Functions" mid-name. `FIELD_SELECT` exists precisely so
 * the cockpit has ONE type scale — putting a DS `Select` here would reintroduce the mismatch that
 * was reported as "the font size is weird". The Clear action IS the atom, because that one is a
 * labelled action button and nothing about it fights the row.
 */
export function RadarFilters({
  filter,
  clusters,
  departments,
  onChange,
  window,
  windowChoices,
  onWindowChange,
  windowBusy,
}: {
  filter: RadarFilter;
  clusters: string[];
  departments: string[];
  onChange: (next: RadarFilter) => void;
  /** Days the interview conversions are measured over; 0 means all history. */
  window: number;
  windowChoices: number[];
  onWindowChange: (next: number) => void;
  /** The window re-queries the warehouse, unlike the two filters, which are client-side. */
  windowBusy: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium text-iron">Cluster</span>
        <select
          className={FIELD_SELECT}
          value={filter.cluster}
          onChange={(e) => onChange({ ...filter, cluster: e.target.value })}
        >
          <option value="">All clusters</option>
          {clusters.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium text-iron">Department</span>
        <select
          className={FIELD_SELECT}
          value={filter.department}
          onChange={(e) => onChange({ ...filter, department: e.target.value })}
        >
          <option value="">All departments</option>
          {departments.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </label>

      {isFiltered(filter) ? (
        <Button variant="secondary" size="sm" onClick={() => onChange({ cluster: '', department: '' })}>
          Clear
        </Button>
      ) : null}

      {/*
        ⚠️ This one is NOT a filter. Cluster and department narrow rows the browser already has;
        this changes what the warehouse measured, so it re-queries — hence the busy state, and
        hence it sits last rather than beside the two that behave differently.
      */}
      <label className="ml-auto flex flex-col gap-1">
        <span className="text-[11px] font-medium text-iron">Conversions measured over</span>
        <select
          className={FIELD_SELECT}
          value={window}
          disabled={windowBusy}
          onChange={(e) => onWindowChange(Number(e.target.value))}
        >
          {windowChoices.map((d) => (
            <option key={d} value={d}>
              {windowLabel(d)}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}


const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * Open hires against the pipelines that carry them, and where the difference goes.
 *
 * The identity is `openRequisitions = measured + unpublished + unmatched`, and every term is
 * rendered whenever it is non-zero — a reconciliation that hides its own remainder does not
 * reconcile anything.
 */
export function PortfolioReconciliation({
  totals,
  filtered,
}: {
  totals: RadarTotals;
  filtered: boolean;
}) {
  const lossy = totals.unpublishedRequisitions + totals.unmatchedRequisitions;

  /*
   * ⚠️ COLLAPSED SINCE Sep 29 2026 (Theresa), and that REVERSES what this component's own note used
   * to say: "It is not collapsed: a total nobody can reconcile is a total nobody trusts, and this
   * one had already been reported as wrong." That was right when the header above it said
   * "22 open roles" over 22 cards counting something else. The header was fixed, the strip did its
   * job, and what is left is five figures and a paragraph of arithmetic sitting above the tab's
   * actual content every single visit.
   *
   * So the reconciliation is one line — the headline count, plus a red flag WHEN something is
   * lossy — and the identity behind it is one click away. ⚠️ The summary keeps the full detail
   * open by DEFAULT while anything is unmatched or unpublished: those are the cases the strip was
   * built for, and hiding them is what it exists to prevent.
   */
  return (
    <details className="group mt-3 overflow-hidden rounded-lg" open={lossy > 0}>
      <summary
        data-testid="radar-reconciliation-summary"
        className={`flex cursor-pointer list-none flex-wrap items-center gap-x-2 px-4 py-2 text-[13px] ${
          lossy ? 'bg-lightRed text-[#8A0E22]' : 'bg-lightBlue text-[#0B4F9E]'
        }`}
      >
        <span
          aria-hidden
          className="inline-block h-2 w-2 border-r-[1.5px] border-b-[1.5px] border-current transition-transform duration-150 group-open:rotate-45"
          style={{ transform: 'rotate(-45deg)' }}
        />
        <span className="tabular-nums">
          <b className="font-semibold">{totals.openRequisitions}</b>{' '}
          open {plural(totals.openRequisitions, 'hire', 'hires')}
          {filtered ? ' in this view' : ''} across{' '}
          <b className="font-semibold">{totals.postings}</b>
          {plural(totals.postings, 'pipeline', 'pipelines')}
        </span>
        {lossy ? (
          <span className="font-medium">
            · {lossy} not measured
          </span>
        ) : (
          <span className="opacity-70">· how this reconciles</span>
        )}
      </summary>

      <FactStrip tone={lossy ? 'red' : 'blue'} testId="radar-reconciliation">
        <Fact>
          <FactValue>{totals.openRequisitions}</FactValue>
          open {plural(totals.openRequisitions, 'hire', 'hires')}
          {filtered ? ' in this view' : ''}
        </Fact>
        <Fact>
          across <FactValue>{totals.postings}</FactValue>
          {plural(totals.postings, 'pipeline', 'pipelines')}
        </Fact>
        <Fact>
          <FactValue>{totals.measuredRequisitions}</FactValue>
          measured on <FactValue>{totals.measuredPostings}</FactValue>
          {plural(totals.measuredPostings, 'card', 'cards')}
        </Fact>
        {totals.unpublishedRequisitions ? (
          <Fact>
            <FactValue>{totals.unpublishedRequisitions}</FactValue>
            on a posting Lever dropped
          </Fact>
        ) : null}
        {totals.unmatchedRequisitions ? (
          <Fact>
            <FactValue>{totals.unmatchedRequisitions}</FactValue>
            with no posting id
          </Fact>
        ) : null}
      </FactStrip>
      <p className="bg-cotton px-4 py-2 text-[11.5px] leading-snug text-iron">
        One card is one <strong className="font-semibold">pipeline</strong> — a single Lever posting,
        however many people it hires. Role Prioritisation counts{' '}
        <strong className="font-semibold">open hires</strong>, so its total is the first figure here
        and not the number of cards below.
      </p>
    </details>
  );
}
